import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Lightbox from '@axa-japan/design-system-react/Lightbox';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { useEffect, useRef, useState } from 'react';
import imageIconUrl from '../../assets//image_icon.svg';
import trashIconUrl from '../../assets//trash_icon.svg';
import wrongFileUrl from '../../assets//wrong_file.svg';
import fileIconUrl from '../../assets/file_icon.svg';
import imgPlaceholderUrl from '../../assets/img_placeholder.svg';
import { useQualityCheckCache } from '../../providers/QualityCheckCacheProvider';
import { ALLOWED_FILE_EXTENSIONS, FILE_DEFAULT_NAME, MAX_FILE_SIZE, VIEWABLE_FILES } from '../../utils/fileUtils';
import { ImageQualityCheckResult } from '../../utils/imageQualityCheck/imageQualityCheck';
import Camera from './Camera';

const QUALITY_CHECK_IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

const isFileViewable = (file: File) =>
  new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = String(e.target?.result);
    };
    fileReader.readAsDataURL(file);
  });

const FileDropzone = ({ onFileChange, fileId, formId }: { onFileChange: (file?: File, src?: string) => void; fileId: string; formId: string }) => {
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (!ALLOWED_FILE_EXTENSIONS.includes(e.target.files[0].type)) {
        setWarningMessage(
          'カメラ撮影・ファイル選択いただいたファイル形式は取扱対象外となります。お手数ですがファイル形式をご確認のうえ、再度、カメラ撮影もしくはファイル選択してください。',
        );
      } else if (e.target.files[0].size > MAX_FILE_SIZE) {
        setWarningMessage('書類のサイズが20MBを超えているため、お取り扱いできません。');
      } else if (VIEWABLE_FILES.includes(e.target.files[0].type) && !(await isFileViewable(e.target.files[0]))) {
        setWarningMessage('撮影またはファイル選択いただいた画像を表示できませんでした。もう一度やり直してください。');
      } else {
        setWarningMessage('');
        onFileChange(e.target.files[0]);
      }
    }
  };

  return (
    <div className="bg-white px-10 pb-5 pt-10 flex flex-col items-center gap-2 rounded-sm border-dashed border-black border-2 border-opacity-30">
      {warningMessage ? (
        <div className="w-3/5 h-full flex flex-col gap-5 pb-5 items-center">
          <img src={wrongFileUrl} />
          <Text color="danger" className="!text-center">
            {warningMessage}
          </Text>
        </div>
      ) : (
        <div className="flex justify-center bg-interactiveHoverGreyTransparent w-3/5 lg:w-2/5 xl:w-1/5 h-52 mb-5">
          <img src={imgPlaceholderUrl} />
        </div>
      )}
      <div className="w-2/3 flex flex-col lg:flex-row justify-between lg:gap-5 gap-2 mb-5">
        <input
          id={`file-uploader-${fileId}`}
          type="file"
          hidden
          onChange={handleFileChange}
          accept=".jpg,.jpeg,.png,.tiff,.pdf,.tif"
          className="bg-blue"
        />
        {isCameraModalOpen && (
          <div className="fixed !z-10 top-0 left-0 w-full h-screen bg-black">
            <Camera onCapture={(file, src) => onFileChange(file, src as string)} onClose={() => setIsCameraModalOpen(false)} formId={formId}></Camera>
          </div>
        )}
        <Button onClick={() => setIsCameraModalOpen(true)} variant="outlined" className="!w-full">
          書類を撮影
        </Button>
        <Button variant="outlined" asChild className="!w-full">
          <label htmlFor={`file-uploader-${fileId}`}>ファイルを選択</label>
        </Button>
      </div>
    </div>
  );
};

const FilePreview = ({
  file,
  onDelete,
  onQualityCheckChange,
}: {
  file: File;
  onDelete: (src: File) => void;
  onQualityCheckChange?: (hasQualityError: boolean) => void;
}) => {
  const { get, getOrCheck } = useQualityCheckCache();

  const shouldRunQualityCheck = QUALITY_CHECK_IMAGE_TYPES.includes(file.type);
  const cachedInitial = shouldRunQualityCheck ? get(file) : undefined;

  const buildErrorMessages = (result: ImageQualityCheckResult): string[] => {
    const errorMessages: string[] = [];
    if (!result.blur) errorMessages.push('画像がぼやけていませんか？');
    if (!result.shadow) errorMessages.push('画像に影が入っていませんか？');
    if (!result.darkness) errorMessages.push('画像の明るさに問題はありませんか？');
    if (!result.boundary) errorMessages.push('書類の全体が読み取れる大きさで写っていますか？');
    if (!result.background) errorMessages.push('書類以外のものが写り込んでいませんか？');
    if (!result.overall) errorMessages.push('overall');
    return errorMessages;
  };

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoadingCheckQuality, setIsLoadingCheckQuality] = useState<boolean>(!cachedInitial && shouldRunQualityCheck);
  const [qualityErrors, setQualityErrors] = useState<string[]>(cachedInitial ? buildErrorMessages(cachedInitial) : []);
  const [qualityCheckFailed, setQualityCheckFailed] = useState<boolean>(false);
  const [previewMaxHeight, setPreviewMaxHeight] = useState<number | undefined>(undefined);
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const qualityCheckedRef = useRef<File | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const landscapeRefRef = useRef<HTMLDivElement>(null);
  const onQualityCheckChangeRef = useRef(onQualityCheckChange);

  useEffect(() => {
    onQualityCheckChangeRef.current = onQualityCheckChange;
  }, [onQualityCheckChange]);

  const fileSrc = URL.createObjectURL(file);

  useEffect(() => {
    if (!shouldRunQualityCheck) {
      onQualityCheckChangeRef.current?.(false);
      return;
    }
    if (qualityCheckedRef.current === file) return;

    const cached = get(file);
    if (cached) {
      qualityCheckedRef.current = file;
      setQualityErrors(buildErrorMessages(cached));
      setQualityCheckFailed(false);
      setIsLoadingCheckQuality(false);
      onQualityCheckChangeRef.current?.(!cached.overall);
      return;
    }

    qualityCheckedRef.current = file;
    setIsLoadingCheckQuality(true);
    setQualityCheckFailed(false);
    setQualityErrors([]);

    getOrCheck(file)
      .then((result) => {
        setQualityErrors(buildErrorMessages(result));
        onQualityCheckChangeRef.current?.(!result.overall);
      })
      .catch(() => {
        setQualityCheckFailed(true);
        onQualityCheckChangeRef.current?.(false);
      })
      .finally(() => {
        setIsLoadingCheckQuality(false);
      });
  }, [file, shouldRunQualityCheck, get, getOrCheck]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setIsPortrait(img.naturalHeight > img.naturalWidth);
  };

  useEffect(() => {
    if (!isPortrait) {
      setPreviewMaxHeight(undefined);
      return;
    }
    const el = landscapeRefRef.current;
    if (!el) return;
    const update = () => setPreviewMaxHeight(el.clientHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPortrait]);

  return (
    <div className="flex lg:flex-row md:flex-col w-full justify-between gap-5">
      <div className="bg-white flex flex-col items-center justify-between w-full px-5 pt-10 pb-5 gap-5 rounded-sm border-dashed border-black border-2 border-opacity-30">
        {file.type === 'application/pdf' && (
          <div className="flex flex-col items-center ">
            <div>
              <img src={fileIconUrl} alt="PDF Icon" className="min-h-28 mx-auto" />
            </div>
            <div>
              <Text className="line-clamp-1">{file.name}</Text>
            </div>
          </div>
        )}

        {file.type === 'image/tiff' && (
          <div className="flex flex-col items-center ">
            <div>
              <img src={imageIconUrl} alt="TIFF Icon" className="min-h-28 mx-auto" />
            </div>
            <div>
              <Text className="line-clamp-1">{file.name}</Text>
            </div>
          </div>
        )}

        {file.type !== 'image/tiff' && file.type !== 'application/pdf' && (
          <div className="flex flex-row items-start justify-center w-full gap-5">
            <div className="w-2/5 flex flex-col items-center gap-2">
              <div ref={containerRef} className="w-full flex items-center justify-center relative">
                <div ref={landscapeRefRef} className="w-full aspect-[3/2] invisible pointer-events-none absolute" aria-hidden="true" />
                <button type="button" onClick={() => setIsPreviewModalOpen(true)} className="p-0 border-0 bg-transparent max-w-full cursor-pointer">
                  <img
                    src={fileSrc}
                    alt="Preview"
                    className="img-preview max-w-full object-contain"
                    style={previewMaxHeight ? { maxHeight: `${previewMaxHeight}px` } : undefined}
                    onLoad={handleImageLoad}
                  />
                </button>
                <Lightbox
                  open={isPreviewModalOpen}
                  onClose={() => setIsPreviewModalOpen(false)}
                  slides={[
                    {
                      src: fileSrc,
                    },
                  ]}
                />
              </div>
              <StatusModal
                id="delete-file-modal"
                title="画像を削除します"
                open={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                content="撮影・ファイル選択したファイルは削除されますがよろしいでしょうか。"
                buttons={[
                  {
                    text: 'キャンセル',
                  },
                  {
                    text: 'OK',
                    onClick: () => onDelete(file),
                  },
                ]}
              />
              <Button variant="text" icon={<img src={trashIconUrl} alt="" />} onClick={() => setIsDeleteModalOpen(true)} iconPosition="left">
                削除
              </Button>
            </div>

            {shouldRunQualityCheck && (
              <div className="flex flex-col gap-2 w-2/5">
                {isLoadingCheckQuality && <Text>画像品質をチェック中...</Text>}
                {!isLoadingCheckQuality && !qualityCheckFailed && (
                  <Text size="lg" weight="bold">
                    画像品質チェック結果:
                    {qualityErrors.includes('overall') ? (
                      <Badge variant="warning" text="確認" className="!ml-2" />
                    ) : (
                      <Badge variant="success" text="OK" className="!ml-2" />
                    )}
                  </Text>
                )}
                {!isLoadingCheckQuality && !qualityCheckFailed && qualityErrors.filter((error) => error !== 'overall').length > 0 && (
                  <ul className="list-disc pl-4">
                    {qualityErrors
                      .filter((error) => error !== 'overall')
                      .map((errorMessage) => (
                        <li key={errorMessage} className="text-statusDanger">
                          {errorMessage}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {(file.type === 'image/tiff' || file.type === 'application/pdf') && (
          <div className="flex flex-col items-center">
            <StatusModal
              id="delete-file-modal"
              title="画像を削除します"
              open={isDeleteModalOpen}
              setOpen={setIsDeleteModalOpen}
              content="撮影・ファイル選択したファイルは削除されますがよろしいでしょうか。"
              buttons={[
                {
                  text: 'キャンセル',
                },
                {
                  text: 'OK',
                  onClick: () => onDelete(file),
                },
              ]}
            />
            <Button variant="text" icon={<img src={trashIconUrl} alt="" />} onClick={() => setIsDeleteModalOpen(true)} iconPosition="left">
              削除
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

type FileUploadProps = {
  file?: File;
  onFileChange: (file?: File, src?: string) => void;
  fileId: string;
  receptionNo: string;
  index: number;
  formId: string;
  onQualityCheckChange?: (hasQualityError: boolean) => void;
};

const FileUpload = ({ onFileChange, file, fileId, receptionNo, index, formId, onQualityCheckChange }: FileUploadProps) => {
  const isPreviewing = Boolean(file && file?.name !== FILE_DEFAULT_NAME);
  const onQualityCheckChangeRef = useRef(onQualityCheckChange);

  useEffect(() => {
    onQualityCheckChangeRef.current = onQualityCheckChange;
  }, [onQualityCheckChange]);

  useEffect(() => {
    if (!isPreviewing) {
      onQualityCheckChangeRef.current?.(false);
    }
  }, [isPreviewing]);

  const handleFileChange = (file?: File) => {
    if (!file) {
      onFileChange(undefined);
      return;
    }
    const seq = String(index + 1).padStart(2, '0');
    const dotIndex = file.name.lastIndexOf('.');
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex + 1) : '';
    const newFileName = ext ? `${receptionNo}_${formId}_${seq}.${ext}` : `${receptionNo}_${formId}_${seq}`;
    const newFile = new File([file], newFileName, { type: file.type, lastModified: file.lastModified });
    onFileChange(newFile);
  };

  const handleFileRemove = () => {
    const file = new File([], FILE_DEFAULT_NAME, { type: 'JPEG' });
    onFileChange(file);
  };

  return isPreviewing && file ? (
    <FilePreview file={file} onDelete={handleFileRemove} onQualityCheckChange={onQualityCheckChange} />
  ) : (
    <FileDropzone fileId={fileId} formId={formId} onFileChange={(file) => handleFileChange(file)} />
  );
};

export default FileUpload;
